import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, Store, Hammer, Sparkle, Layers, ShieldCheck, Clock, Users, Award, ArrowRight, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { services } from "@/data/services";

const HERO_IMG = "https://images.pexels.com/photos/9300768/pexels-photo-9300768.jpeg";
const OFFICE_IMG = "https://images.pexels.com/photos/5882572/pexels-photo-5882572.jpeg";

const stats = [
  { value: "150+", label: "Clients satisfaits" },
  { value: "10 ans", label: "d'expertise" },
  { value: "24h", label: "Devis rapide" },
  { value: "100%", label: "Produits éco-responsables" },
];

const trustPoints = [
  "Équipes formées et certifiées",
  "Produits écologiques certifiés",
  "Interventions 7j/7",
  "Assurance responsabilité civile",
];

export default function Home() {
  const iconMap = { Building2, Store, Hammer, Sparkle, Layers };
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          <div className="reveal-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#E3F2FD] text-[#1976D2] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2196F3] animate-pulse" />
              Nettoyage professionnel — Île-de-France
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.02]">
              Des espaces
              <span className="relative inline-block mx-3">
                <span className="relative z-10 text-[#2196F3]">impeccables</span>
                <span aria-hidden className="absolute inset-x-0 bottom-2 h-3 bg-[#64B5F6]/40 -z-0 rounded-sm" />
              </span>
              pour des équipes performantes.
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed">
              GIGA CLEAN SERVICE assure la propreté de vos bureaux, commerces et locaux avec des équipes expertes, des produits éco-responsables et une exigence sans compromis.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/devis" data-testid="hero-devis-cta">
                <Button size="lg" className="rounded-full bg-[#2196F3] hover:bg-[#1976D2] text-white px-8 h-12 text-base font-semibold">
                  Obtenir un devis gratuit
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-services-cta">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8 border-slate-300 text-slate-900">
                  Découvrir nos services
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-4 max-w-md">
              {trustPoints.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                  <div className="h-5 w-5 rounded-full bg-[#E3F2FD] flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-[#2196F3]" strokeWidth={3} />
                  </div>
                  {p}
                </div>
              ))}
            </div>
          </div>

          {/* Hero image collage */}
          <div className="relative reveal-up">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-[#2196F3]/20">
              <img src={HERO_IMG} alt="Équipe de nettoyage professionnel en action" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1929]/60 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="absolute -bottom-8 -left-6 md:-left-12 bg-white rounded-2xl shadow-xl border border-slate-100 p-5 w-56"
            >
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#2196F3] flex items-center justify-center">
                  <Award className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-display font-bold text-slate-900">Certifié Qualité</div>
                  <div className="text-xs text-slate-500">Normes AFNOR</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="hidden md:block absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl border border-slate-100 p-5"
            >
              <div className="text-3xl font-display font-extrabold text-[#2196F3]">4.9<span className="text-lg text-slate-400">/5</span></div>
              <div className="text-xs text-slate-500 mt-1">Satisfaction clients</div>
            </motion.div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative border-y border-slate-100 bg-slate-50/50">
          <div className="mx-auto max-w-7xl px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center md:text-left">
                <div className="font-display font-extrabold text-3xl md:text-4xl text-[#2196F3]">{s.value}</div>
                <div className="text-sm text-slate-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-4">Nos prestations</div>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 max-w-2xl">
                Une expertise complète pour chaque environnement professionnel.
              </h2>
            </div>
            <Link to="/services">
              <Button variant="outline" className="rounded-full" data-testid="home-see-all-services">
                Voir tous les services <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon] || Sparkle;
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: i * 0.05 }}
                  className="group relative bg-white rounded-2xl border border-slate-100 p-8 hover:border-[#2196F3]/40 hover:shadow-xl hover:shadow-[#2196F3]/5 hover:-translate-y-1"
                  style={{ transitionProperty: "box-shadow, transform, border-color", transitionDuration: "300ms" }}
                >
                  <div className="h-14 w-14 rounded-2xl bg-[#E3F2FD] flex items-center justify-center mb-6 group-hover:bg-[#2196F3]" style={{ transitionProperty: "background-color", transitionDuration: "300ms" }}>
                    <Icon className="h-7 w-7 text-[#2196F3] group-hover:text-white" strokeWidth={2} style={{ transitionProperty: "color", transitionDuration: "300ms" }} />
                  </div>
                  <h3 className="font-display font-bold text-xl text-slate-900 mb-3">{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{s.short}</p>
                  <Link to="/services" className="mt-6 inline-flex items-center text-sm font-semibold text-[#2196F3] hover:text-[#1976D2]" data-testid={`service-card-${s.slug}`}>
                    En savoir plus <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 md:py-32 bg-[#f0f7ff]">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={OFFICE_IMG} alt="Bureaux professionnels propres" className="rounded-3xl w-full aspect-[4/3] object-cover shadow-2xl shadow-[#2196F3]/20" />
            <div className="absolute -bottom-8 -right-8 bg-[#2196F3] text-white rounded-2xl p-6 max-w-[220px] shadow-xl">
              <div className="font-display font-extrabold text-4xl">10+</div>
              <div className="text-sm text-white/90 mt-1">années d'excellence au service des entreprises</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-4">Pourquoi nous choisir</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-8">
              Une méthode rigoureuse, des résultats garantis.
            </h2>
            <div className="space-y-6">
              {[
                { icon: ShieldCheck, title: "Sécurité et hygiène", desc: "Nos protocoles respectent les normes AFNOR et les recommandations sanitaires." },
                { icon: Users, title: "Équipes dédiées", desc: "Un chef d'équipe pour chaque site et des agents formés régulièrement." },
                { icon: Clock, title: "Ponctualité", desc: "Interventions selon vos plages horaires, sans perturber votre activité." },
              ].map(({ icon: Icon, title, desc }, i) => (
                <div key={i} className="flex gap-5">
                  <div className="h-12 w-12 rounded-xl bg-white border border-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-[#2196F3]" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg text-slate-900">{title}</h3>
                    <p className="text-slate-600 text-sm mt-1 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-[#1976D2] p-10 md:p-16 grain">
            <div className="relative z-10 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white leading-tight">
                  Prêt à retrouver des locaux impeccables ?
                </h2>
                <p className="text-white/80 mt-5 max-w-xl text-lg">
                  Recevez un devis personnalisé en moins de 24 heures — sans engagement.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link to="/devis" data-testid="home-cta-devis">
                  <Button size="lg" className="rounded-full bg-white text-[#1976D2] hover:bg-[#64B5F6] hover:text-white h-14 px-10 text-base font-semibold">
                    Demander mon devis
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div aria-hidden className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#64B5F6]/30 blur-3xl" />
          </div>
        </div>
      </section>
    </div>
  );
}
